import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import StudentLayout from "@/components/StudentLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { BarChart3, Loader2, CheckCircle2, Clock3, Lock, ChevronRight, Star, ArrowLeft, Vote } from "lucide-react";

const STATUS_BADGE = {
  active: { l: "ACTIVE", c: "bg-emerald-100 text-emerald-700" },
  closed: { l: "CLOSED", c: "bg-rose-100 text-rose-700" },
  voted: { l: "VOTED", c: "bg-sky-100 text-sky-700" },
};

function closingSoon(endAt) {
  if (!endAt) return false;
  const diff = new Date(endAt).getTime() - Date.now();
  return diff > 0 && diff < 48 * 3600 * 1000;
}
function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
}

export default function StudentPolls() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("active");
  const [openPoll, setOpenPoll] = useState(null);

  const load = async () => {
    try { const r = await api.get("/student/polls"); setData(r.data); }
    catch (e) { toast.error(apiErr(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <StudentLayout><div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div></StudentLayout>;

  const lists = { active: data.active, voted: data.voted, closed: data.closed };
  const current = lists[tab] || [];

  return (
    <StudentLayout>
      <div className="max-w-3xl mx-auto space-y-5" data-testid="student-polls-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-[#0b2a6b] dark:text-amber-400" /> Polls & Surveys
          </h1>
          <p className="text-sm text-slate-500 mt-1">Share your voice — vote in your school's polls.</p>
        </div>

        <div className="flex gap-1.5">
          {[["active", `Active${data.pending ? ` (${data.pending})` : ""}`], ["voted", "Voted"], ["closed", "Closed"]].map(([t, l]) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`student-polls-tab-${t}`}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${tab === t ? "bg-[#0b2a6b] text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
          ))}
        </div>

        {current.length === 0 ? (
          <div className="text-center py-16 text-slate-400" data-testid="student-polls-empty">
            <Vote className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p>{tab === "active" ? "No polls awaiting your vote right now." : `No ${tab} polls.`}</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {current.map((p) => (
              <button key={p.id} onClick={() => setOpenPoll(p.id)} data-testid={`student-poll-card-${p.id}`}
                className="text-left bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 hover:shadow-lg hover:border-[#0b2a6b]/40 transition group">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${p.voted ? STATUS_BADGE.voted.c : STATUS_BADGE[p.effective_status]?.c || "bg-slate-100"}`}>
                        {p.voted && p.effective_status !== "closed" ? "VOTED" : STATUS_BADGE[p.effective_status]?.l}
                      </span>
                      {p.effective_status === "active" && !p.voted && closingSoon(p.end_at) && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">CLOSING SOON</span>
                      )}
                    </div>
                    <div className="font-bold text-slate-800 dark:text-white text-lg leading-snug">{p.title}</div>
                    {p.description && <div className="text-sm text-slate-500 mt-1 line-clamp-2">{p.description}</div>}
                    {p.end_at && <div className="text-xs text-slate-400 mt-2 flex items-center gap-1"><Clock3 className="h-3 w-3" /> Closes {fmtDate(p.end_at)}</div>}
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-[#0b2a6b] shrink-0 mt-1" />
                </div>
                <div className="mt-3">
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0b2a6b] dark:text-amber-400">
                    {p.effective_status === "closed" ? "View" : p.voted ? "View / change vote" : "Vote now"} <ChevronRight className="h-4 w-4" />
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {openPoll && <PollVoteSheet pollId={openPoll} onClose={() => setOpenPoll(null)} onDone={() => { setOpenPoll(null); load(); }} />}
    </StudentLayout>
  );
}

function PollVoteSheet({ pollId, onClose, onDone }) {
  const [poll, setPoll] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    api.get(`/student/polls/${pollId}`).then((r) => {
      const p = r.data.poll;
      setPoll(p);
      const init = {};
      (p.my_answers || []).forEach((a) => {
        init[a.question_id] = { option_ids: a.option_ids || [], other_text: a.other_text || "", rating: a.rating, text: a.text || "" };
      });
      setAnswers(init);
      if (p.can_see_results) loadResults();
    }).catch((e) => { toast.error(apiErr(e?.response?.data?.detail)); onClose(); });
  }, [pollId]);

  const loadResults = () => api.get(`/student/polls/${pollId}/results`).then((r) => setResults(r.data)).catch(() => {});

  const setA = (qid, patch) => setAnswers((a) => ({ ...a, [qid]: { ...(a[qid] || {}), ...patch } }));

  const editable = poll && poll.effective_status === "active" && (!poll.voted || poll.settings.allow_change_vote);

  const submit = async () => {
    setSubmitting(true);
    const payload = { answers: (poll.questions || []).map((q) => {
      const a = answers[q.id] || {};
      return { question_id: q.id, option_ids: a.option_ids || [], other_text: a.other_text || "", rating: a.rating ?? null, text: a.text || "" };
    }) };
    try {
      const r = await api.post(`/student/polls/${pollId}/vote`, payload);
      toast.success(r.data.changed ? "Your vote was updated ✓" : "Vote submitted successfully ✓");
      setConfirmOpen(false);
      const p = r.data.poll;
      setPoll(p);
      if (p.can_see_results) { await loadResults(); setShowResults(true); }
      else onDone();
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail)); setConfirmOpen(false); }
    setSubmitting(false);
  };

  if (!poll) return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-white" /></div>
  );

  const closed = poll.effective_status === "closed";

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="student-vote-sheet">
      <div className="bg-white dark:bg-slate-900 w-full sm:max-w-lg sm:rounded-2xl rounded-t-3xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-slate-900 border-b p-4 flex items-center gap-3 z-10">
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="vote-back"><ArrowLeft className="h-5 w-5" /></button>
          <div className="flex-1 font-bold text-slate-800 dark:text-white truncate">{poll.title}</div>
        </div>

        <div className="p-5 space-y-6">
          {poll.description && <p className="text-sm text-slate-500">{poll.description}</p>}

          {(showResults || (closed && poll.can_see_results)) && results ? (
            <ResultsView results={results} />
          ) : closed ? (
            <div className="text-center py-8 text-slate-400"><Lock className="h-8 w-8 mx-auto mb-2" /><p>This poll is closed.{!poll.can_see_results && " Results are not shared."}</p></div>
          ) : (
            <>
              {poll.questions.map((q, qi) => (
                <QuestionInput key={q.id} q={q} qi={qi} value={answers[q.id] || {}} onChange={(patch) => setA(q.id, patch)} disabled={!editable} />
              ))}
              {poll.voted && !poll.settings.allow_change_vote ? (
                <div className="text-sm text-center bg-slate-50 rounded-xl p-3 text-slate-500 flex items-center justify-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" /> Your response has been recorded and cannot be changed.
                </div>
              ) : editable && (
                <Button className="w-full bg-[#0b2a6b] hover:bg-[#153e94] h-12 text-base" onClick={() => setConfirmOpen(true)} data-testid="vote-submit-btn">
                  {poll.voted ? "Update my vote" : "Submit vote"}
                </Button>
              )}
              {poll.can_see_results && results && (
                <button onClick={() => setShowResults(true)} className="w-full text-sm text-[#0b2a6b] font-semibold" data-testid="vote-view-results">View current results</button>
              )}
            </>
          )}
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="vote-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Submit your vote?</AlertDialogTitle>
            <AlertDialogDescription>
              {poll.settings.allow_change_vote ? "You can change it later while the poll is open." : "Your response cannot be changed after submitting."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Review</AlertDialogCancel>
            <AlertDialogAction className="bg-[#0b2a6b] hover:bg-[#153e94]" onClick={submit} disabled={submitting} data-testid="vote-confirm-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yes, submit"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function QuestionInput({ q, qi, value, onChange, disabled }) {
  return (
    <div data-testid={`vote-q-${qi}`}>
      <div className="font-semibold text-slate-800 dark:text-white mb-3">{qi + 1}. {q.text}{q.required && <span className="text-rose-500">*</span>}</div>

      {(q.type === "single" || q.type === "yesno") && (
        <div className="space-y-2">
          {q.options.map((o) => {
            const sel = (value.option_ids || [])[0] === o.id;
            return (
              <button key={o.id} disabled={disabled} onClick={() => onChange({ option_ids: [o.id], other_text: "" })} data-testid={`vote-q-${qi}-opt-${o.id}`}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 transition ${sel ? "border-[#0b2a6b] bg-[#0b2a6b]/5 font-semibold" : "border-slate-200 hover:border-slate-300"}`}>
                {o.label}
              </button>
            );
          })}
          {q.allow_other && (
            <OtherOption qi={qi} selected={(value.option_ids || [])[0] === "__other__"} value={value.other_text}
              onSelect={() => onChange({ option_ids: ["__other__"] })} onText={(t) => onChange({ option_ids: ["__other__"], other_text: t })} disabled={disabled} />
          )}
        </div>
      )}

      {q.type === "multiple" && (
        <div className="space-y-2">
          {q.options.map((o) => {
            const sel = (value.option_ids || []).includes(o.id);
            const toggle = () => onChange({ option_ids: sel ? value.option_ids.filter((x) => x !== o.id) : [...(value.option_ids || []), o.id] });
            return (
              <button key={o.id} disabled={disabled} onClick={toggle} data-testid={`vote-q-${qi}-opt-${o.id}`}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 transition flex items-center gap-3 ${sel ? "border-[#0b2a6b] bg-[#0b2a6b]/5 font-semibold" : "border-slate-200"}`}>
                <span className={`h-4 w-4 rounded border ${sel ? "bg-[#0b2a6b] border-[#0b2a6b]" : "border-slate-300"}`} />{o.label}
              </button>
            );
          })}
          {q.allow_other && (
            <OtherOption qi={qi} multiple selected={!!value.other_text || (value.option_ids || []).includes("__other__")} value={value.other_text}
              onSelect={() => onChange({ other_text: value.other_text || " " })} onText={(t) => onChange({ other_text: t })} disabled={disabled} />
          )}
        </div>
      )}

      {q.type === "rating" && (
        <div className="flex gap-2">
          {Array.from({ length: q.rating_max || 5 }).map((_, i) => {
            const n = i + 1; const on = (value.rating || 0) >= n;
            return (
              <button key={n} disabled={disabled} onClick={() => onChange({ rating: n })} data-testid={`vote-q-${qi}-rating-${n}`}
                className="p-1"><Star className={`h-9 w-9 ${on ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} /></button>
            );
          })}
        </div>
      )}

      {q.type === "text" && (
        <Textarea rows={3} value={value.text || ""} onChange={(e) => onChange({ text: e.target.value })} disabled={disabled} placeholder="Type your answer…" data-testid={`vote-q-${qi}-text`} />
      )}
    </div>
  );
}

function OtherOption({ qi, selected, value, onSelect, onText, disabled, multiple }) {
  return (
    <div>
      <button disabled={disabled} onClick={onSelect} data-testid={`vote-q-${qi}-other`}
        className={`w-full text-left px-4 py-3 rounded-xl border-2 transition ${selected ? "border-[#0b2a6b] bg-[#0b2a6b]/5 font-semibold" : "border-slate-200"}`}>
        Other — please specify
      </button>
      {selected && <Input className="mt-2" value={(value || "").trim()} onChange={(e) => onText(e.target.value)} placeholder="Your answer" disabled={disabled} data-testid={`vote-q-${qi}-other-text`} />}
    </div>
  );
}

function ResultsView({ results }) {
  const COLORS = ["#0b2a6b", "#D4AF37", "#2563eb", "#059669", "#dc2626", "#7c3aed"];
  return (
    <div className="space-y-6" data-testid="vote-results-view">
      <div className="text-sm text-slate-500 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> {results.total_responses} response(s) so far</div>
      {results.questions.map((q) => (
        <div key={q.question_id}>
          <div className="font-semibold text-slate-800 dark:text-white mb-2">{q.text}</div>
          {q.type === "rating" ? (
            <div className="text-sm text-slate-600">Average rating: <span className="font-bold text-[#0b2a6b]">{q.average}</span> / {q.rating_max}</div>
          ) : (q.options || []).length ? (
            <div className="space-y-2">
              {q.options.map((o, i) => (
                <div key={o.id || i}>
                  <div className="flex justify-between text-xs mb-0.5"><span>{o.label}</span><span className="font-semibold">{o.percentage}%</span></div>
                  <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${o.percentage}%`, background: COLORS[i % COLORS.length] }} /></div>
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-slate-400">Responses are collected privately.</p>}
        </div>
      ))}
    </div>
  );
}
